import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, FlaskConical, Copy, Layout, Percent, Target, Link, AlertCircle, Save, ExternalLink, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import landingPageService from '../../services/landingPageService';
import abTestService, { ABTest } from '../../services/abTestService';

interface ABTestModalProps {
  isOpen: boolean;
  onClose: () => void;
  landingPageId?: string;
  landingPageName?: string;
  onTestCreated?: () => void;
  editTest?: ABTest | null;
  onTestUpdated?: () => void;
}

const ABTestModal: React.FC<ABTestModalProps> = ({
  isOpen,
  onClose,
  landingPageId: initialPageId,
  landingPageName: initialPageName,
  onTestCreated,
  editTest,
  onTestUpdated
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [allPages, setAllPages] = useState<{ id: string; name: string; slug: string; published_url?: string }[]>([]);

  const isEditMode = !!editTest;
  const canEditFullForm = !editTest || editTest.status === 'draft';

  // Form state
  const [testName, setTestName] = useState('');
  const [testSlug, setTestSlug] = useState('');
  const [notes, setNotes] = useState('');
  const [selectedControlId, setSelectedControlId] = useState(initialPageId || '');
  const [goal, setGoal] = useState<'conversions' | 'views' | 'time_on_page'>('conversions');
  const [variantSource, setVariantSource] = useState<'create' | 'existing'>('create');
  const [selectedVariantId, setSelectedVariantId] = useState('');
  const [trafficSplit, setTrafficSplit] = useState(50);

  const selectedControlPage = allPages.find(p => p.id === selectedControlId);
  // Only show published pages for variant selection
  const availableVariantPages = allPages.filter(p => p.id !== selectedControlId && p.published_url);
  // Only show published pages for control selection
  const publishedPages = allPages.filter(p => p.published_url);

  // Load all pages
  useEffect(() => {
    const loadPages = async () => {
      try {
        const response = await landingPageService.getLandingPages({ limit: 100 });
        if (response.success && response.data) {
          const pages = response.data.map((p: any) => ({
            id: p.id,
            name: p.name,
            slug: p.slug,
            published_url: p.published_url
          }));
          setAllPages(pages);
        }
      } catch (error) {
        console.error('Error loading pages:', error);
      }
    };

    if (isOpen) {
      loadPages();
    }
  }, [isOpen]);

  // Reset form when modal opens or populate with edit data
  useEffect(() => {
    if (isOpen) {
      if (editTest) {
        // Edit mode: populate with existing test data
        setTestName(editTest.name);
        setTestSlug(editTest.slug);
        setNotes(editTest.notes || '');
        setSelectedControlId(editTest.landing_page_id);
        setGoal(editTest.goal);
        // Get traffic split from control variant
        const controlVariant = editTest.variants?.find(v => v.is_control);
        setTrafficSplit(controlVariant?.traffic_percentage || 50);
      } else {
        // Create mode: reset form
        setSelectedControlId(initialPageId || '');
        setTestName(initialPageName ? `Teste A/B - ${initialPageName}` : '');
        setTestSlug('');
        setNotes('');
        setGoal('conversions');
        setVariantSource('create');
        setSelectedVariantId('');
        setTrafficSplit(50);
      }
    }
  }, [isOpen, initialPageId, initialPageName, editTest]);

  // Update test name and slug when control page changes
  useEffect(() => {
    if (selectedControlPage) {
      if (!testName) {
        setTestName(`Teste A/B - ${selectedControlPage.name}`);
      }
      if (!testSlug) {
        setTestSlug(`teste-${selectedControlPage.slug}`);
      }
    }
  }, [selectedControlPage, testName, testSlug]);

  // Generate slug from test name
  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 50);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!testName.trim()) {
      toast.error('Enter the test name');
      return;
    }

    setIsLoading(true);
    try {
      if (isEditMode && editTest) {
        // Update existing test
        const response = await abTestService.updateTest(editTest.id, {
          name: testName,
          notes: notes || undefined
        });

        if (response.success) {
          toast.success(t('abTesting.testUpdated', 'A/B test updated successfully!'));
          onTestUpdated?.();
          onClose();
        } else {
          throw new Error(response.message);
        }
      } else {
        // Create new test
        if (!selectedControlId) {
          toast.error('Select the control page');
          setIsLoading(false);
          return;
        }

        if (variantSource === 'existing' && !selectedVariantId) {
          toast.error('Select an existing page for the variant');
          setIsLoading(false);
          return;
        }

        const response = await abTestService.createTest(selectedControlId, {
          name: testName,
          slug: testSlug || generateSlug(testName),
          goal,
          createVariant: variantSource === 'create',
          variantPageId: variantSource === 'existing' ? selectedVariantId : undefined,
          trafficSplit: { control: trafficSplit }
        });

        if (response.success) {
          toast.success(t('abTesting.testCreated', 'A/B test created successfully!'));
          onTestCreated?.();
          onClose();
        } else {
          throw new Error(response.message);
        }
      }
    } catch (error: any) {
      console.error('Error saving test:', error);
      toast.error(error.response?.data?.message || error.message || 'Error saving test');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const goalOptions = [
    { value: 'conversions', label: t('abTesting.goals.conversions', 'Conversoes (formularios)') },
    { value: 'views', label: t('abTesting.goals.views', 'Visualizacoes') },
    { value: 'time_on_page', label: t('abTesting.goals.timeOnPage', 'Tempo na pagina') }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center">
              <FlaskConical className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                {isEditMode
                  ? t('abTesting.editTest', 'Editar Teste A/B')
                  : t('abTesting.createTest', 'Criar Teste A/B')
                }
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {editTest?.landingPage?.name || initialPageName || selectedControlPage?.name}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Page Previews - Show in edit mode */}
          {isEditMode && editTest?.variants && editTest.variants.length >= 2 && (
            <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Eye className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase">
                  {t('abTesting.pagePreview', 'Paginas do Teste')}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-2">
                {editTest.variants.map((variant) => (
                  <a
                    key={variant.id}
                    href={variant.landingPage?.published_url || `/landing-pages/${variant.landing_page_id}/edit`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      variant.is_control
                        ? 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:border-gray-300 hover:shadow-sm'
                        : 'bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 hover:border-indigo-300 hover:shadow-sm'
                    }`}
                  >
                    <ExternalLink className={`w-4 h-4 flex-shrink-0 ${variant.is_control ? 'text-gray-500' : 'text-indigo-500'}`} />
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm font-medium truncate ${variant.is_control ? 'text-gray-700 dark:text-gray-300' : 'text-indigo-700 dark:text-indigo-300'}`}>
                        {variant.landingPage?.name || variant.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {variant.is_control ? t('abTesting.control', 'Controle') : t('abTesting.variant', 'Variante')} - {variant.traffic_percentage}%
                      </p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Control Page Selection - only show if no initial page provided and not edit mode */}
          {!initialPageId && !isEditMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                {t('abTesting.selectControl', 'Pagina de Controle')}
              </label>
              {publishedPages.length === 0 ? (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  <span className="text-sm text-amber-700 dark:text-amber-300">
                    {t('abTesting.noPublishedPages', 'Nenhuma pagina publicada. Publique uma pagina primeiro.')}
                  </span>
                </div>
              ) : (
                <Select
                  options={[
                    { value: '', label: t('abTesting.selectControlPlaceholder', 'Selecione a pagina de controle...') },
                    ...publishedPages.map(p => ({ value: p.id, label: `${p.name} (publicada)` }))
                  ]}
                  value={selectedControlId}
                  onChange={(e) => setSelectedControlId(e.target.value)}
                />
              )}
            </div>
          )}

          {/* Test Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('abTesting.testName', 'Nome do Teste')}
            </label>
            <Input
              value={testName}
              onChange={(e) => setTestName(e.target.value)}
              placeholder="Ex: Teste de headline"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t('abTesting.notes', 'Notas')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('abTesting.notesPlaceholder', 'Adicione notas sobre este teste...')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Test Slug (URL) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Link className="w-4 h-4 inline mr-1" />
              {t('abTesting.testSlug', 'URL do Teste')}
            </label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500 whitespace-nowrap">/ab/t/</span>
              <Input
                value={testSlug}
                onChange={(e) => !isEditMode && setTestSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
                placeholder="meu-teste-ab"
                className="flex-1"
                disabled={isEditMode}
              />
            </div>
            {!isEditMode && (
              <p className="text-xs text-gray-500 mt-1">
                {t('abTesting.slugHelp', 'Esta URL redirecionara visitantes para uma das versoes')}
              </p>
            )}
          </div>

          {/* Goal */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Target className="w-4 h-4 inline mr-1" />
              {t('abTesting.goal', 'Objetivo do Teste')}
            </label>
            <Select
              options={goalOptions}
              value={goal}
              onChange={(e) => !isEditMode && setGoal(e.target.value as typeof goal)}
              disabled={isEditMode}
            />
          </div>

          {/* Variant Source - only show in create mode */}
          {!isEditMode && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('abTesting.variantSource', 'Pagina Variante')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setVariantSource('create')}
                    className={`p-4 border rounded-xl flex flex-col items-center gap-2 transition-all ${
                      variantSource === 'create'
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Copy className={`w-6 h-6 ${variantSource === 'create' ? 'text-indigo-600' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${variantSource === 'create' ? 'text-indigo-600' : 'text-gray-600 dark:text-gray-400'}`}>
                      {t('abTesting.createVariant', 'Criar copia')}
                    </span>
                    <span className="text-xs text-gray-500 text-center">
                      {t('abTesting.createVariantDesc', 'Duplicar a pagina de controle')}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setVariantSource('existing')}
                    className={`p-4 border rounded-xl flex flex-col items-center gap-2 transition-all ${
                      variantSource === 'existing'
                        ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Layout className={`w-6 h-6 ${variantSource === 'existing' ? 'text-indigo-600' : 'text-gray-400'}`} />
                    <span className={`text-sm font-medium ${variantSource === 'existing' ? 'text-indigo-600' : 'text-gray-600 dark:text-gray-400'}`}>
                      {t('abTesting.selectExisting', 'Usar existente')}
                    </span>
                    <span className="text-xs text-gray-500 text-center">
                      {t('abTesting.selectExistingDesc', 'Escolher pagina ja criada')}
                    </span>
                  </button>
                </div>
              </div>

              {/* Existing Page Selection */}
              {variantSource === 'existing' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('abTesting.selectVariant', 'Selecione a pagina variante')}
                  </label>
                  <Select
                    options={[
                      { value: '', label: 'Selecione uma pagina...' },
                      ...availableVariantPages.map(p => ({ value: p.id, label: p.name }))
                    ]}
                    value={selectedVariantId}
                    onChange={(e) => setSelectedVariantId(e.target.value)}
                  />
                </div>
              )}
            </>
          )}

          {/* Traffic Split */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Percent className="w-4 h-4 inline mr-1" />
              {t('abTesting.trafficSplit', 'Divisao de Trafego')}
            </label>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <input
                  type="range"
                  min="10"
                  max="90"
                  step="5"
                  value={trafficSplit}
                  onChange={(e) => !isEditMode && setTrafficSplit(Number(e.target.value))}
                  disabled={isEditMode}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700 disabled:opacity-50"
                />
              </div>
            </div>
            <div className="flex justify-between mt-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {t('abTesting.control', 'Controle')}: {trafficSplit}%
              </span>
              <span className="text-sm text-indigo-600 dark:text-indigo-400">
                {t('abTesting.variant', 'Variante')}: {100 - trafficSplit}%
              </span>
            </div>
          </div>

          {/* Info Box - only in create mode */}
          {!isEditMode && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 space-y-2">
              <p className="text-sm font-medium text-blue-700 dark:text-blue-300">
                {t('abTesting.howItWorks', 'Como funciona:')}
              </p>
              <ul className="text-sm text-blue-600 dark:text-blue-400 list-disc list-inside space-y-1">
                <li>{t('abTesting.infoStep1', 'Visitantes que acessarem a URL do teste serao redirecionados automaticamente')}</li>
                <li>{t('abTesting.infoStep2', 'O sistema distribui o trafego conforme a porcentagem definida')}</li>
                <li>{t('abTesting.infoStep3', 'Cada visitante sempre ve a mesma versao (via cookie)')}</li>
              </ul>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button
              type="submit"
              isLoading={isLoading}
              leftIcon={isEditMode ? <Save className="w-4 h-4" /> : <FlaskConical className="w-4 h-4" />}
            >
              {isEditMode
                ? t('common.save', 'Salvar')
                : t('abTesting.createTest', 'Criar Teste')
              }
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ABTestModal;
